import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import logo from './logo.svg';
import './TraineeLanding.css';

const API_BASE = process.env.REACT_APP_TRAINEE_API_BASE || 'https://api.pacificpearltraining.site';
const STORAGE_KEY = 'vasisTraineeLandingSession';
const TRAINING_RESTAURANT_TAG = 'First Wok NY Style';
const TRAINING_RESTAURANT_LABEL = 'First Wok Always';
const FEEDBACK_TYPES = [
  'I found an error',
  'I am stuck',
  'Video or page problem',
  'General feedback',
];

const readStoredSession = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
  } catch {
    return null;
  }
};

const saveStoredSession = (session) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
};

const clearStoredSession = () => {
  localStorage.removeItem(STORAGE_KEY);
};

const formatDateTime = (value) => {
  if (!value) return 'Not started';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not started';
  return date.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const percent = (completed, target) => {
  if (!target) return 0;
  return Math.max(0, Math.min(100, Math.round((completed / target) * 100)));
};

const fallbackMilestones = [
  { id: 'first-order', target: 1, label: 'First order', focus: 'Find the item, add it cleanly, and submit without hesitation.' },
  { id: 'menu-rhythm', target: 10, label: 'Menu rhythm', focus: 'Build speed on common menu searches and quantity changes.' },
  { id: 'request-control', target: 25, label: 'Special requests', focus: 'Keep modifier choices accurate while moving faster.' },
  { id: 'ready-check', target: 50, label: 'Ready check', focus: 'Finish the full run with steady accuracy and consistent pace.' },
];

const fallbackScenarios = [
  { id: 'basic-item', range: [0, 1], title: 'Basic item order', cue: 'Start with one simple item and focus on the submit flow.' },
  { id: 'common-repeats', range: [1, 10], title: 'Common repeat orders', cue: 'Repeat simple items until search, cart review, and helper comparison feel automatic.' },
  { id: 'side-choice', range: [10, 25], title: 'Side and option choices', cue: 'Slow down for side choices, sauces, and special requests before you speed up again.' },
  { id: 'full-speed', range: [25, 50], title: 'Full-speed mixed practice', cue: 'Work like a live shift: scan, choose, verify, submit, compare, then repeat.' },
];

const trendLabel = (value, { lowerIsBetter = false } = {}) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return 'No trend yet';
  const numeric = Number(value);
  if (numeric === 0) return 'Steady';
  const isBetter = lowerIsBetter ? numeric < 0 : numeric > 0;
  const sign = numeric > 0 ? '+' : '';
  return `${isBetter ? 'Improved' : 'Needs focus'} ${sign}${numeric}`;
};

function TraineeLanding() {
  const storedSession = useMemo(readStoredSession, []);
  const [email, setEmail] = useState(storedSession?.email || '');
  const [landingData, setLandingData] = useState(null);
  const [formError, setFormError] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [isStarting, setIsStarting] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [videoReadyToComplete, setVideoReadyToComplete] = useState(false);
  const [maxWatchedSeconds, setMaxWatchedSeconds] = useState(0);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackEmail, setFeedbackEmail] = useState(storedSession?.email || '');
  const [feedbackType, setFeedbackType] = useState(FEEDBACK_TYPES[0]);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedbackError, setFeedbackError] = useState('');
  const [feedbackStatus, setFeedbackStatus] = useState('');
  const videoRef = useRef(null);

  const progress = landingData?.progress || null;
  const customer = landingData?.customer || null;
  const config = landingData?.config || {};
  const targetOrders = progress?.helperOrders?.target || config.targetOrders || 50;
  const completedOrders = progress?.helperOrders?.completed || 0;
  const orderProgressPercent = percent(completedOrders, targetOrders);
  const milestones = config.milestones?.length ? config.milestones : fallbackMilestones;
  const practiceScenarios = config.practiceScenarios?.length ? config.practiceScenarios : fallbackScenarios;
  const nextMilestone = progress?.guidance?.nextMilestone
    || milestones.find((milestone) => completedOrders < milestone.target)
    || milestones[milestones.length - 1];
  const currentScenario = progress?.guidance?.currentScenario
    || practiceScenarios.find((scenario) => {
      const [start, end] = scenario.range || [0, targetOrders];
      return completedOrders >= start && completedOrders < end;
    })
    || practiceScenarios[practiceScenarios.length - 1];
  const trend = progress?.guidance?.trend || {};
  const videoComplete = progress?.steps?.video?.status === 'completed';
  const firstOrderComplete = progress?.steps?.pos?.status === 'completed';
  const trainingComplete = completedOrders >= targetOrders;
  const currentSession = landingData
    ? { email: landingData.customer.email, accessCode: landingData.progress.accessCode }
    : storedSession;
  const trainingRestaurantLabel = config.restaurantLabel || TRAINING_RESTAURANT_LABEL;

  const completedTasks = [
    Boolean(progress),
    videoComplete,
    firstOrderComplete,
    trainingComplete,
  ].filter(Boolean).length;

  const openFeedback = () => {
    setFeedbackEmail(customer?.email || currentSession?.email || email || '');
    setFeedbackError('');
    setFeedbackStatus('');
    setFeedbackOpen(true);
  };

  const fetchJson = useCallback(async (url, options = {}) => {
    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(options.headers || {}),
      },
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.error || 'Request failed.');
    }
    return payload;
  }, []);

  const refreshProgress = useCallback(async ({ silent = false } = {}) => {
    const session = readStoredSession();
    if (!session?.email) return null;

    if (!silent) {
      setIsRefreshing(true);
      setStatusMessage('');
    }

    try {
      const params = new URLSearchParams({
        email: session.email,
        accessCode: session.accessCode || '',
      });
      const payload = await fetchJson(`${API_BASE}/api/trainee-landing/progress?${params.toString()}`);
      setLandingData(payload);
      saveStoredSession({
        email: payload.customer.email,
        accessCode: payload.progress.accessCode,
      });
      return payload;
    } catch (error) {
      if (!silent) setFormError(error.message);
      return null;
    } finally {
      if (!silent) setIsRefreshing(false);
    }
  }, [fetchJson]);

  useEffect(() => {
    if (storedSession?.email) {
      refreshProgress({ silent: true });
    }
  }, [refreshProgress, storedSession?.email]);

  useEffect(() => {
    if (!landingData) return undefined;
    const intervalId = window.setInterval(() => {
      refreshProgress({ silent: true });
    }, 15000);
    return () => window.clearInterval(intervalId);
  }, [landingData, refreshProgress]);

  const startAccount = async (event) => {
    event.preventDefault();
    setFormError('');
    setStatusMessage('');

    if (!email.trim()) {
      setFormError('Enter the email address you want to use for training.');
      return;
    }
    setIsStarting(true);
    try {
      const payload = await fetchJson(`${API_BASE}/api/trainee-landing/start`, {
        method: 'POST',
        body: JSON.stringify({
          email,
          restaurantTag: TRAINING_RESTAURANT_TAG,
        }),
      });

      setLandingData(payload);
      saveStoredSession({
        email: payload.customer.email,
        accessCode: payload.progress.accessCode,
      });
      setEmail(payload.customer.email);
      setStatusMessage(payload.created
        ? 'Training ID created. When the POS task unlocks, use Open POS here and sign in with this same email.'
        : 'Training ID loaded. Your progress is synced from Shopify.');
    } catch (error) {
      setFormError(error.message);
    } finally {
      setIsStarting(false);
    }
  };

  const recordEvent = async (eventType, payload = {}) => {
    if (!currentSession?.email) return null;

    const result = await fetchJson(`${API_BASE}/api/trainee-landing/events`, {
      method: 'POST',
      body: JSON.stringify({
        email: currentSession.email,
        accessCode: currentSession.accessCode,
        eventType,
        payload,
      }),
    });
    setLandingData(result);
    saveStoredSession({
      email: result.customer.email,
      accessCode: result.progress.accessCode,
    });
    return result;
  };

  const completeVideo = async () => {
    const video = videoRef.current;
    const durationSeconds = video?.duration && Number.isFinite(video.duration) ? video.duration : 0;
    const watchedSeconds = Math.max(maxWatchedSeconds, durationSeconds);

    try {
      setFormError('');
      await recordEvent('video_completed', {
        watchedSeconds,
        durationSeconds,
      });
      setStatusMessage('Video complete. The POS task is unlocked.');
    } catch (error) {
      setFormError(error.message);
    }
  };

  const openPos = async () => {
    const destination = config.posUrl || 'https://order-taking-2026-66217e6b27756711050e.o2.myshopify.dev/agent-dashboard';

    try {
      setFormError('');
      await recordEvent('pos_opened');
    } catch (error) {
      setFormError(error.message);
    }
    window.location.assign(destination);
  };

  const openOrderHelper = async () => {
    if (!customer?.id) return;

    try {
      setFormError('');
      await recordEvent('helper_opened');
      const payload = await fetchJson(`${API_BASE}/login`, {
        method: 'POST',
        body: JSON.stringify({
          shopifyId: customer.id,
          tags: customer.tags || [],
        }),
      });
      if (payload.url) {
        window.location.assign(payload.url);
      }
    } catch (error) {
      setFormError(error.message);
    }
  };

  const signOutLanding = () => {
    clearStoredSession();
    setLandingData(null);
    setStatusMessage('');
    setFormError('');
    setVideoReadyToComplete(false);
    setMaxWatchedSeconds(0);
  };

  const submitFeedback = async (event) => {
    event.preventDefault();
    setFeedbackError('');
    setFeedbackStatus('');

    if (!feedbackMessage.trim() || feedbackMessage.trim().length < 8) {
      setFeedbackError('Write a short note so we know what to check.');
      return;
    }

    setFeedbackSubmitting(true);
    try {
      await fetchJson(`${API_BASE}/api/trainee-landing/feedback`, {
        method: 'POST',
        body: JSON.stringify({
          email: feedbackEmail || currentSession?.email || email,
          accessCode: currentSession?.accessCode,
          feedbackType,
          message: feedbackMessage,
          pageUrl: window.location.href,
          progress: {
            completedTasks,
            completedOrders,
            targetOrders,
            latestScore: progress?.stats?.latestScore,
            averageScore: progress?.stats?.averageScore,
          },
        }),
      });
      setFeedbackStatus('Feedback sent. Thank you - we will review it.');
      setFeedbackMessage('');
      window.setTimeout(() => setFeedbackOpen(false), 900);
    } catch (error) {
      setFeedbackError(error.message);
    } finally {
      setFeedbackSubmitting(false);
    }
  };

  const onVideoTimeUpdate = () => {
    const video = videoRef.current;
    if (!video) return;
    const nextWatched = Math.max(maxWatchedSeconds, video.currentTime || 0);
    setMaxWatchedSeconds(nextWatched);
    if (video.duration && Number.isFinite(video.duration)) {
      setVideoReadyToComplete(nextWatched >= video.duration * 0.96);
    }
  };

  const taskRows = [
    {
      number: '01',
      title: 'Create Training ID',
      status: progress ? 'complete' : 'active',
      detail: progress
        ? `${customer?.email || email} is connected to ${trainingRestaurantLabel}.`
        : 'Enter your email so the system can create your trainee account.',
    },
    {
      number: '02',
      title: 'Watch POS Walkthrough',
      status: videoComplete ? 'complete' : progress ? 'active' : 'locked',
      detail: videoComplete
        ? `Completed ${formatDateTime(progress?.steps?.video?.completedAt)}.`
        : 'Watch the full order-taking walkthrough before opening the POS.',
    },
    {
      number: '03',
      title: 'Create First POS Order',
      status: firstOrderComplete ? 'complete' : videoComplete ? 'active' : 'locked',
      detail: firstOrderComplete
        ? `First order detected ${formatDateTime(progress?.steps?.pos?.firstOrderCompletedAt)}.`
        : 'Open the new POS from this page, sign in with this same email, and submit one order.',
    },
    {
      number: '04',
      title: 'Complete 50 Practice Calls',
      status: trainingComplete ? 'complete' : firstOrderComplete ? 'active' : 'locked',
      detail: `${completedOrders} of ${targetOrders} scored practice calls completed.`,
    },
  ];

  return (
    <main className="trainee-page">
      <section className="trainee-shell">
        <div className="trainee-brand-row">
          <img src={logo} alt="Pacific Pearl" className="trainee-logo" />
          <div>
            <p className="trainee-kicker">Pacific Pearl training</p>
            <h1>Order-taking readiness</h1>
          </div>
          <div className="trainee-header-actions">
            <button type="button" className="text-button help-button" onClick={openFeedback}>
              Feedback / Help
            </button>
            {landingData && (
              <button type="button" className="text-button" onClick={signOutLanding}>
                Change email
              </button>
            )}
          </div>
        </div>

        <div className="trainee-intro">
          <div>
            <h2>Learn the POS in measured steps.</h2>
            <p>
              We train restaurant order-taking agents to move accurately through live menu workflows.
              This first exercise measures how quickly you learn the POS, how carefully you handle
              special requests, and how your accuracy improves over repeated practice.
            </p>
          </div>
          <div className="trainee-progress-summary">
            <span>{completedTasks}/4 tasks</span>
            <div className="summary-track" aria-hidden="true">
              <div style={{ width: `${percent(completedTasks, 4)}%` }} />
            </div>
          </div>
        </div>

        <section className="purpose-panel" aria-label="Why 50 orders">
          <p className="section-label">Why 50 orders?</p>
          <h2>We measure how quickly you learn accurate order-taking.</h2>
          <p>
            The 50 practice calls are how Pacific Pearl evaluates readiness. We are looking at
            accuracy, special-request handling, speed, consistency, and whether your score improves
            as you practice. This helps us understand your potential and decide whether you should
            continue to the next hiring step.
          </p>
        </section>

        {!landingData && (
          <form className="trainee-entry-panel" onSubmit={startAccount}>
            <div>
              <label htmlFor="trainee-email">Email</label>
              <input
                id="trainee-email"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="you@example.com"
                autoComplete="email"
              />
            </div>
            <div>
              <label htmlFor="trainee-restaurant">Training restaurant</label>
              <input
                id="trainee-restaurant"
                value={trainingRestaurantLabel}
                readOnly
                aria-readonly="true"
              />
            </div>
            <button type="submit" disabled={isStarting}>
              {isStarting ? 'Creating ID...' : 'Start Training'}
            </button>
          </form>
        )}

        {(formError || statusMessage) && (
          <div className={formError ? 'trainee-alert error' : 'trainee-alert'}>
            {formError || statusMessage}
          </div>
        )}

        {landingData && (
          <>
            <section className="video-workspace" aria-labelledby="training-video-title">
              <div className="video-copy">
                <p className="section-label">Task 02</p>
                <h2 id="training-video-title">Watch the POS walkthrough</h2>
                <p>
                  This video shows the complete process for taking an order in the POS,
                  from finding items to reviewing special requests and submitting the order.
                </p>
              </div>
              <div className="video-frame">
                {config.videoUrl ? (
                  <video
                    ref={videoRef}
                    controls
                    playsInline
                    preload="metadata"
                    src={config.videoUrl}
                    onTimeUpdate={onVideoTimeUpdate}
                    onEnded={() => setVideoReadyToComplete(true)}
                  />
                ) : (
                  <div className="video-placeholder">
                    <span>Training video pending</span>
                    <p>The walkthrough will appear here as soon as the training video is ready.</p>
                  </div>
                )}
              </div>
              <div className="video-actions">
                <button
                  type="button"
                  onClick={completeVideo}
                  disabled={videoComplete || !config.videoUrl || !videoReadyToComplete}
                >
                  {videoComplete ? 'Video Complete' : 'Complete Video Task'}
                </button>
              </div>
            </section>

            <section className="task-grid" aria-label="Training tasks">
              {taskRows.map((task) => (
                <article className={`task-tile ${task.status}`} key={task.number}>
                  <div className="task-number">{task.number}</div>
                  <div>
                    <h3>{task.title}</h3>
                    <p>{task.detail}</p>
                  </div>
                  <span className="task-status">{task.status}</span>
                </article>
              ))}
            </section>

            {trainingComplete && (
              <section className="completion-panel" aria-label="Training complete">
                <p className="section-label">Training run complete</p>
                <h2>Congratulations, you finished the 50 calls.</h2>
                <p>
                  We will use your scores, timing, and special-request accuracy to understand your
                  potential and decide whether you will continue to the next step. If anything felt
                  wrong or confusing, use the Feedback / Help button so we can review it.
                </p>
              </section>
            )}

            <section className="next-focus" aria-label="Next practice focus">
              <div>
                <p className="section-label">Next focus</p>
                <h2>{currentScenario?.title || 'Practice run'}</h2>
                <p>{currentScenario?.cue || 'Keep working through the practice calls.'}</p>
              </div>
              <div className="focus-card">
                <span>{nextMilestone?.label || 'Next milestone'}</span>
                <strong>
                  {nextMilestone?.remaining === 0
                    ? 'Complete'
                    : `${nextMilestone?.remaining ?? 0} to go`}
                </strong>
                <p>{nextMilestone?.focus}</p>
              </div>
            </section>

            <section className="practice-console">
              <div className="console-main">
                <p className="section-label">Practice run</p>
                <h2>{completedOrders} / {targetOrders} scored orders</h2>
                <div className="order-track" aria-label={`${orderProgressPercent}% complete`}>
                  <div style={{ width: `${orderProgressPercent}%` }} />
                </div>
                <div className="metric-row">
                  <div>
                    <span>Average score</span>
                    <strong>{progress?.stats?.averageScore ?? 'N/A'}</strong>
                  </div>
                  <div>
                    <span>Latest score</span>
                    <strong>{progress?.stats?.latestScore ?? 'N/A'}</strong>
                  </div>
                  <div>
                    <span>Best score</span>
                    <strong>{progress?.stats?.bestScore ?? 'N/A'}</strong>
                  </div>
                </div>
                <div className="milestone-rail" aria-label="Practice milestones">
                  {milestones.map((milestone) => {
                    const milestonePercent = percent(Math.min(completedOrders, milestone.target), milestone.target);
                    const status = completedOrders >= milestone.target
                      ? 'complete'
                      : completedOrders > 0 && completedOrders < milestone.target
                        ? 'active'
                        : 'pending';
                    return (
                      <div className={`milestone ${status}`} key={milestone.id || milestone.target}>
                        <div className="milestone-topline">
                          <span>{milestone.label}</span>
                          <strong>{Math.min(completedOrders, milestone.target)}/{milestone.target}</strong>
                        </div>
                        <div className="milestone-track" aria-label={`${milestonePercent}% of ${milestone.label}`}>
                          <div style={{ width: `${milestonePercent}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="console-actions">
                <button type="button" onClick={openPos} disabled={!videoComplete}>
                  Open POS
                </button>
                <button type="button" onClick={openOrderHelper} disabled={!firstOrderComplete}>
                  Start Practice Calls
                </button>
                <button type="button" className="secondary-action" onClick={() => refreshProgress()} disabled={isRefreshing}>
                  {isRefreshing ? 'Refreshing...' : 'Refresh Progress'}
                </button>
              </div>
            </section>

            <section className="trend-panel" aria-label="Practice trend">
              <div>
                <span>Latest 5 avg</span>
                <strong>{trend.latestFiveScoreAverage ?? 'N/A'}</strong>
              </div>
              <div>
                <span>Previous 5 avg</span>
                <strong>{trend.previousFiveScoreAverage ?? 'N/A'}</strong>
              </div>
              <div>
                <span>Accuracy trend</span>
                <strong>{trendLabel(trend.scoreDelta)}</strong>
              </div>
              <div>
                <span>Time penalty trend</span>
                <strong>{trendLabel(trend.timePenaltyDelta, { lowerIsBetter: true })}</strong>
              </div>
            </section>

            <section className="recent-orders">
              <div>
                <p className="section-label">Latest scored orders</p>
                <h2>Progress history</h2>
              </div>
              <div className="order-list">
                {(progress?.orders || []).slice(0, 8).map((order) => (
                  <div className="order-row" key={order.traineeOrderId || order.traineeOrderName}>
                    <span>{order.traineeOrderName || `Order ${order.traineeOrderId}`}</span>
                    <span>{formatDateTime(order.traineeOrderCreatedAt)}</span>
                    <strong>{order.score ?? 'N/A'}</strong>
                    {Number(order.mistakeCount || 0) > 0 && (
                      <em>{order.mistakeCount} {order.mistakeCount === 1 ? 'mistake' : 'mistakes'}</em>
                    )}
                  </div>
                ))}
                {(progress?.orders || []).length === 0 && (
                  <div className="order-empty">
                    Scored practice calls will appear here after the first comparison.
                  </div>
                )}
              </div>
            </section>
          </>
        )}
      </section>
      {feedbackOpen && (
        <div className="feedback-overlay" role="dialog" aria-modal="true" aria-labelledby="feedback-title">
          <form className="feedback-modal" onSubmit={submitFeedback}>
            <div className="feedback-modal-header">
              <div>
                <p className="section-label">Feedback / Help</p>
                <h2 id="feedback-title">Tell us what happened</h2>
              </div>
              <button type="button" className="feedback-close" onClick={() => setFeedbackOpen(false)}>
                Close
              </button>
            </div>
            <label>
              Email
              <input
                type="email"
                value={feedbackEmail}
                onChange={(event) => setFeedbackEmail(event.target.value)}
                placeholder="you@example.com"
              />
            </label>
            <label>
              Type
              <select value={feedbackType} onChange={(event) => setFeedbackType(event.target.value)}>
                {FEEDBACK_TYPES.map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </label>
            <label>
              Message
              <textarea
                value={feedbackMessage}
                onChange={(event) => setFeedbackMessage(event.target.value)}
                placeholder="Tell us what is wrong, where you got stuck, or what we should improve."
                rows={5}
              />
            </label>
            {feedbackError && <div className="feedback-error">{feedbackError}</div>}
            {feedbackStatus && <div className="feedback-success">{feedbackStatus}</div>}
            <div className="feedback-actions">
              <button type="button" className="secondary-action" onClick={() => setFeedbackOpen(false)}>
                Cancel
              </button>
              <button type="submit" disabled={feedbackSubmitting}>
                {feedbackSubmitting ? 'Sending...' : 'Send Feedback'}
              </button>
            </div>
          </form>
        </div>
      )}
    </main>
  );
}

export default TraineeLanding;
